import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Heart, Loader2, ShieldAlert, Sparkles, Check } from 'lucide-react';
import { toast } from 'sonner';
import { api } from './api';
import { MaternalBackLink, MaternalDisclaimer } from './_ui';

/**
 * Maternal · Daily Check-in
 *
 * One calm, mobile-first form: mood / energy / sleep / symptoms / notes.
 * Backend: POST /api/maternal/checkin → returns score + triage + reassurance.
 * After saving, the same page renders the gentle response (status, focus
 * line, triage badge) — no toast-and-bounce.
 *
 * Symptoms can be pre-selected via `?symptoms=fatigue,nausea` (used by the
 * Symptoms Tracker page → "Log a full check-in" CTA).
 */

const MOODS = [
  { v: 'happy', label: 'Happy',  emoji: '😊' },
  { v: 'okay',  label: 'Okay',   emoji: '🙂' },
  { v: 'low',   label: 'Low',    emoji: '😔' },
];
const ENERGY = [
  { v: 'high',   label: 'High',    emoji: '⚡' },
  { v: 'medium', label: 'Steady',  emoji: '🌿' },
  { v: 'low',    label: 'Low',     emoji: '🪫' },
];
const SLEEP = [
  { v: 'good',   label: 'Restful', emoji: '😴' },
  { v: 'broken', label: 'Broken',  emoji: '🌙' },
  { v: 'poor',   label: 'Poor',    emoji: '😣' },
];

// Same taxonomy the backend recognises; grouped here for the UI.
const SYMPTOM_GROUPS = [
  {
    title: 'Common',
    description: "Often part of a normal pregnancy or recovery.",
    tone: 'green',
    items: [
      { v: 'nausea', label: 'Nausea' },
      { v: 'fatigue', label: 'Fatigue' },
      { v: 'tender_breasts', label: 'Tender breasts' },
      { v: 'back_pain', label: 'Back pain' },
      { v: 'heartburn', label: 'Heartburn' },
      { v: 'constipation', label: 'Constipation' },
      { v: 'headache_mild', label: 'Mild headache' },
      { v: 'swelling_mild', label: 'Mild swelling' },
      { v: 'cramps_mild', label: 'Mild cramps' },
    ],
  },
  {
    title: 'Worth monitoring',
    description: "Common, but worth watching. Hydrate, rest, and re-check tomorrow.",
    tone: 'amber',
    items: [
      { v: 'nausea_severe', label: 'Severe nausea' },
      { v: 'swelling_face_hands', label: 'Face / hand swelling' },
      { v: 'heartburn_severe', label: 'Severe heartburn' },
      { v: 'anxiety', label: 'Anxiety' },
      { v: 'sleep_loss', label: 'Sleep loss' },
    ],
  },
  {
    title: 'Need care now',
    description: "These signs aren't routine — please contact your clinician today.",
    tone: 'rose',
    items: [
      { v: 'bleeding', label: 'Bleeding' },
      { v: 'severe_headache', label: 'Severe headache' },
      { v: 'vision_changes', label: 'Vision changes' },
      { v: 'reduced_movement', label: 'Reduced movement' },
      { v: 'leaking_fluid', label: 'Leaking fluid' },
      { v: 'calf_pain', label: 'Calf pain or swelling' },
      { v: 'severe_abdominal_pain', label: 'Severe abdominal pain' },
      { v: 'fever_high', label: 'Fever > 38°C' },
    ],
  },
];

const TONE_PILL = {
  green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  amber: 'bg-amber-50 text-amber-800 border-amber-200',
  rose:  'bg-rose-50 text-rose-700 border-rose-200',
};

const TONE_CHIP = {
  // unselected
  green: 'bg-white border-emerald-200 text-emerald-800 hover:bg-emerald-50',
  amber: 'bg-white border-amber-200 text-amber-800 hover:bg-amber-50',
  rose:  'bg-white border-rose-200 text-rose-700 hover:bg-rose-50',
};
const TONE_CHIP_ON = {
  green: 'bg-emerald-600 border-emerald-600 text-white shadow-sm',
  amber: 'bg-amber-500 border-amber-500 text-white shadow-sm',
  rose:  'bg-rose-600 border-rose-600 text-white shadow-sm',
};

const TRIAGE_PILL = {
  green: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  amber: 'bg-amber-50 text-amber-900 border-amber-200',
  red:    'bg-rose-50 text-rose-800 border-rose-200',
};

const RowGroup = ({ label, value, options, onChange, testid }) => (
  <div data-testid={testid}>
    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">{label}</p>
    <div className="grid grid-cols-3 gap-2">
      {options.map((o) => {
        const active = value === o.v;
        return (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(o.v)}
            className={[
              'rounded-2xl border-2 px-2 py-3 text-sm font-semibold transition-all',
              active
                ? 'border-rose-500 bg-rose-50 text-rose-700 shadow-sm'
                : 'border-gray-100 bg-white text-gray-700 hover:border-rose-200',
            ].join(' ')}
            data-testid={`${testid}-${o.v}`}
            aria-pressed={active}
          >
            <span className="block text-2xl mb-0.5" aria-hidden="true">{o.emoji}</span>
            {o.label}
          </button>
        );
      })}
    </div>
  </div>
);

const MaternalDailyCheckin = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const preset = useMemo(() => {
    const s = params.get('symptoms') || '';
    return s ? new Set(s.split(',').map((x) => x.trim()).filter(Boolean)) : new Set();
  }, [params]);

  const [mood, setMood] = useState('okay');
  const [energy, setEnergy] = useState('medium');
  const [sleep, setSleep] = useState('broken');
  const [symptoms, setSymptoms] = useState(() => new Set(preset));
  const [notes, setNotes] = useState('');
  const [snap, setSnap] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.snapshot().then(setSnap).catch(() => setSnap(null));
  }, []);

  const toggleSymptom = (v) => {
    setSymptoms((prev) => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v); else next.add(v);
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!snap?.has_profile) {
      toast.error('Please set your maternal stage first.');
      navigate('/maternal-health');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        mood, energy, sleep,
        symptoms: Array.from(symptoms),
        notes: notes.trim() || null,
      };
      const res = await api.postCheckin(payload);
      setResult(res);
      // Smooth scroll up so the result lands above the fold.
      if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save your check-in. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- POST-SAVE RESULT VIEW ----------
  if (result) {
    const triage = result.triage || { level: 'green', title: 'Check-in saved', message: '', details: [] };
    const score = result.score ?? 0;
    return (
      <div className="min-h-screen bg-gradient-to-b from-rose-50/40 via-white to-white pb-24" data-testid="maternal-checkin-result">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
          <button
            onClick={() => navigate('/maternal-health')}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Maternal Health
          </button>

          <div className="rounded-2xl bg-white border-2 border-rose-100 shadow-sm p-6 mb-4">
            <div className="flex items-start gap-4">
              <span className="w-12 h-12 rounded-2xl bg-rose-100 text-rose-700 flex items-center justify-center shrink-0">
                <Heart className="h-6 w-6" />
              </span>
              <div className="flex-1">
                <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-1">Today's check-in</p>
                <h1 className="text-2xl font-bold text-gray-900 leading-tight">Saved · {score}/100</h1>
                {result.reassurance && (
                  <p className="text-sm text-gray-700 leading-relaxed mt-2 italic">{result.reassurance}</p>
                )}
              </div>
            </div>
          </div>

          <div className={`rounded-2xl border-2 p-5 mb-4 ${TRIAGE_PILL[triage.level]}`} data-testid={`maternal-triage-${triage.level}`}>
            <div className="flex items-start gap-3">
              {triage.level === 'red' ? <ShieldAlert className="h-5 w-5 mt-0.5 shrink-0" /> : <Sparkles className="h-5 w-5 mt-0.5 shrink-0" />}
              <div className="min-w-0">
                <p className="font-bold leading-tight">{triage.title}</p>
                <p className="text-sm leading-relaxed mt-1">{triage.message}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => navigate('/maternal-health')}
              className="inline-flex items-center gap-1.5 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-xl px-5 py-3 transition-all"
              data-testid="maternal-checkin-back-cta"
            >
              Back to Hub
            </button>
            <button
              onClick={() => { setResult(null); setSymptoms(new Set()); setNotes(''); }}
              className="inline-flex items-center gap-1.5 bg-white border-2 border-gray-200 hover:border-rose-300 text-gray-800 font-semibold rounded-xl px-5 py-3 transition-all"
              data-testid="maternal-checkin-redo-cta"
            >
              Log another
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- FORM ----------
  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50/40 via-white to-white pb-24" data-testid="maternal-checkin">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <MaternalBackLink />

        <header className="mb-5">
          <div className="flex items-center gap-3 mb-1">
            <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center shrink-0">
              <Heart className="h-5 w-5 text-white" />
            </span>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">Daily check-in</h1>
              <p className="text-xs text-gray-500 mt-0.5">Mood, energy, sleep, and how you feel today — under a minute.</p>
            </div>
          </div>
        </header>

        <form onSubmit={handleSubmit} className="space-y-5">
          <RowGroup label="Mood today"  value={mood}   options={MOODS}  onChange={setMood}   testid="maternal-mood" />
          <RowGroup label="Energy"      value={energy} options={ENERGY} onChange={setEnergy} testid="maternal-energy" />
          <RowGroup label="Last night's sleep" value={sleep} options={SLEEP} onChange={setSleep} testid="maternal-sleep" />

          {/* Symptom groups */}
          <div data-testid="maternal-symptoms">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">
              Anything you're feeling?
            </p>
            <p className="text-xs text-gray-500 mb-3">
              Tap any that apply. We use these to give you calm, stage-aware feedback — never a diagnosis.
            </p>

            <div className="space-y-4">
              {SYMPTOM_GROUPS.map((g) => (
                <div key={g.title}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-md border ${TONE_PILL[g.tone]}`}>
                      {g.title}
                    </span>
                    <p className="text-xs text-gray-500 leading-snug">{g.description}</p>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {g.items.map((it) => {
                      const on = symptoms.has(it.v);
                      return (
                        <button
                          key={it.v}
                          type="button"
                          onClick={() => toggleSymptom(it.v)}
                          className={[
                            'inline-flex items-center gap-1 text-xs font-medium rounded-full px-3 py-1.5 border-2 transition-all',
                            on ? TONE_CHIP_ON[g.tone] : TONE_CHIP[g.tone],
                          ].join(' ')}
                          data-testid={`maternal-symptom-${it.v}`}
                          aria-pressed={on}
                        >
                          {on && <Check className="h-3 w-3" />}
                          {it.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="maternal-notes" className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2 block">
              Notes (optional)
            </label>
            <textarea
              id="maternal-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="Anything you want to remember about today…"
              className="w-full rounded-xl border-2 border-gray-100 bg-white p-3 text-sm leading-relaxed focus:border-rose-300 focus:outline-none transition-colors"
              data-testid="maternal-notes"
            />
            <p className="text-[10px] text-gray-400 text-right mt-1">{notes.length}/500</p>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting || !snap?.has_profile}
            className="w-full bg-gradient-to-br from-rose-500 to-pink-500 hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold rounded-xl px-5 py-4 shadow-sm transition-all"
            data-testid="maternal-checkin-submit"
          >
            {submitting ? <Loader2 className="h-5 w-5 inline-block animate-spin mr-2" /> : null}
            {submitting ? 'Saving…' : 'Save check-in'}
          </button>

          {!snap?.has_profile && (
            <p className="text-xs text-rose-700 text-center">
              You need to set your stage first. <button type="button" onClick={() => navigate('/maternal-health')} className="underline">Go to setup</button>.
            </p>
          )}

          <MaternalDisclaimer />
        </form>
      </div>
    </div>
  );
};

export default MaternalDailyCheckin;